import { computed, nextTick, ref, watch, ComputedRef, Ref } from 'vue';
import { RowSelectionState } from '@tanstack/vue-table';
import { merge } from 'lodash-es';
import type { ClickedRowInfo } from '@/common/utils';
import type { Emits, Props } from '../../modernGrid.setup';
import type {
  GridRowDataType,
  GridRawRowDataType,
  GridTableInstance,
  GridTableInstanceOptions,
  GridRowInstance,
  GridRowSelectionOption,
  GridTreeViewOption,
} from '../../modernGrid.types';
import { defaultRowSelectionOption } from '../../modernGrid.define';
import { getRawRows, getRowIdByData } from '../../modernGrid.utils';

export const useRowSelection = <RowDataType extends GridRawRowDataType>({
  props,
  emit,
  table,
  rowsMapById,
  expandingKey,
  treeViewInfo,
}: {
  props: Props<RowDataType>;
  emit: Emits<RowDataType>;
  table: Ref<GridTableInstance<RowDataType> | undefined>;
  rowsMapById: Ref<Map<string, GridRowDataType<RowDataType>> | undefined>;
  expandingKey: ComputedRef<string>;
  treeViewInfo: ComputedRef<GridTreeViewOption>;
}) => {
  const rowSelectionInfo = computed<GridRowSelectionOption>(() =>
    merge({}, defaultRowSelectionOption, props.option?.rowSelection),
  );
  const rowSelectionState = ref<RowSelectionState>({});
  const clickedRowId = ref<string>('');

  const highlightedRowIds = computed<string[]>(
    () =>
      props.highlightedRows?.map(
        (row) => getRowIdByData<RowDataType>(row, rowsMapById.value, expandingKey.value) || '',
      ) || [],
  );

  const selectedRowId = computed<string>({
    get: () =>
      props.selectedRow && rowSelectionInfo.value.enableSelect
        ? getRowIdByData(props.selectedRow, rowsMapById.value, expandingKey.value) || ''
        : '',
    set: (rowId: string) => {
      const rowData = rowsMapById.value?.get(rowId);
      if (rowData && rowSelectionInfo.value.enableSelect) {
        const { rowId: _, ...rowDataWithoutId } = rowData;
        emit('update:selected-row', rowDataWithoutId as unknown as RowDataType);
      }
    },
  });

  const checkedRows = computed<RowDataType[]>({
    get: () => props.checkedRows || [],
    set: (rows: RowDataType[]) => emit('update:checked-rows', rows),
  });

  const selectRow = (rowId: string) => {
    if (rowSelectionInfo.value.enableSelect === false) return;

    // disableRootNodeSelect: 트리 구조에서 최상위 노드(depth === 0) 선택 방지
    if (rowSelectionInfo.value.disableRootNodeSelect) {
      const row = table.value?.getRow(rowId);
      if (row && row.depth === 0) return;
    }

    const isAlreadySelected = selectedRowId.value === rowId;

    if (!isAlreadySelected) {
      selectedRowId.value = rowId;
      clickedRowId.value = rowId;
    } else if (!rowSelectionInfo.value.disableDeselect) {
      emit('update:selected-row', undefined);
      clickedRowId.value = '';
    } else {
      clickedRowId.value = rowId;
    }
  };

  const onClickRow = (
    rowInfo: ClickedRowInfo<GridRowDataType<RowDataType>> & { rowId: string },
    isDisabled: boolean = false,
  ) => {
    if (isDisabled || rowSelectionInfo.value.enableSelect === false) return;
    const { event, rowData, rowId, rowIndex, cellIndex, cellName } = rowInfo;

    // disableRootNodeSelect: 트리 구조에서 최상위 노드(depth === 0) 선택 및 클릭 이벤트 방지
    if (rowSelectionInfo.value.disableRootNodeSelect) {
      const row = table.value?.getRow(rowId);
      if (row && row.depth === 0) return;
    }

    const isAlreadySelected = selectedRowId.value === rowId;

    if (!isAlreadySelected) {
      selectedRowId.value = rowId;
      clickedRowId.value = rowId;
    } else if (!rowSelectionInfo.value.disableDeselect) {
      emit('update:selected-row', undefined);
      clickedRowId.value = '';
    } else {
      clickedRowId.value = rowId;
    }
    emit('click-row', {
      event,
      rowData,
      rowIndex,
      cellIndex,
      cellName,
    });
  };

  /**
   * 트리 구조에서 부모 row의 선택 상태를 자식 상태와 동기화
   * 자식 중 하나라도 해제되면 부모도 해제 (uncheckable 자식 제외)
   */
  const synchronizeParentSelection = (
    state: RowSelectionState,
    tableInstance: GridTableInstance<RowDataType> | undefined,
    uncheckableRowIds?: Set<string>,
  ): RowSelectionState => {
    if (!tableInstance) return state;

    const newState = { ...state };
    const allRows = tableInstance.getRowModel().flatRows;

    allRows.forEach((row) => {
      if (row.subRows && row.subRows.length > 0) {
        // uncheckable 자식은 "모두 선택됨" 체크에서 제외
        const checkableChildren = uncheckableRowIds
          ? row.subRows.filter((child) => !uncheckableRowIds.has(child.id))
          : row.subRows;

        // 체크 가능한 자식이 없으면 부모 상태 유지
        if (checkableChildren.length === 0) {
          return;
        }

        const allCheckableChildrenSelected = checkableChildren.every(
          (child) => newState[child.id] === true,
        );

        if (newState[row.id] && !allCheckableChildrenSelected) {
          newState[row.id] = false;
        }
      }
    });

    return newState;
  };

  const isRowUncheckable = (row: GridRowInstance<RowDataType>): boolean => {
    if (!props.uncheckableRows?.length) return false;

    const rowOriginal = row.original as Record<string, unknown>;

    return props.uncheckableRows.some((uncheckableRow) => {
      const uncheckableObj = { ...uncheckableRow } as Record<string, unknown>;
      const keys = Object.keys(uncheckableObj).filter((k) => k !== 'rowId');

      return keys.every((key) => {
        const rowValue = rowOriginal[key];
        const uncheckableValue = uncheckableObj[key];

        if (Array.isArray(rowValue) && Array.isArray(uncheckableValue)) {
          return JSON.stringify(rowValue) === JSON.stringify(uncheckableValue);
        }

        return rowValue === uncheckableValue;
      });
    });
  };

  const isDetailRow = (row: GridRowInstance<RowDataType>): boolean => {
    const rowOriginal = row.original as Record<string, unknown>;
    return rowOriginal.isDetail === true;
  };

  const updateRowSelectionState = (
    updater: RowSelectionState | ((state: RowSelectionState) => RowSelectionState),
  ) => {
    let newState = typeof updater === 'function' ? updater(rowSelectionState.value) : updater;

    let uncheckableRowIds: Set<string> | undefined;
    if (props.uncheckableRows?.length) {
      const allRows = table.value?.getRowModel().flatRows ?? [];
      uncheckableRowIds = new Set(
        allRows.filter((row) => isRowUncheckable(row)).map((row) => row.id),
      );

      const filteredState: RowSelectionState = {};
      Object.entries(newState).forEach(([rowId, isSelected]) => {
        if (!uncheckableRowIds!.has(rowId)) {
          filteredState[rowId] = isSelected;
        }
      });
      newState = filteredState;
    }

    const isEachMode = rowSelectionInfo.value.mode === 'each';
    const isParentOnlyMode = rowSelectionInfo.value.mode === 'parentOnly';
    if (
      !isEachMode &&
      !isParentOnlyMode &&
      rowSelectionInfo.value.enableSubRowCheck &&
      treeViewInfo.value?.use
    ) {
      newState = synchronizeParentSelection(newState, table.value, uncheckableRowIds);
    }

    const { limitCount } = rowSelectionInfo.value;
    if (limitCount && limitCount > 0) {
      const selectedIds = Object.keys(newState).filter((key) => newState[key]);
      if (selectedIds.length > limitCount) {
        const limitedState: RowSelectionState = {};
        selectedIds.slice(-limitCount).forEach((id) => {
          limitedState[id] = true;
        });
        newState = limitedState;
      }
    }

    rowSelectionState.value = newState;
    isInternalCheckedRowsUpdate.value = true;
    checkedRows.value = getRawRows(rowSelectionState.value, rowsMapById.value);
    nextTick(() => {
      isInternalCheckedRowsUpdate.value = false;
    });
  };

  const rowSelectionOptions = computed<Partial<GridTableInstanceOptions<RowDataType>>>(() => {
    const isEachMode = rowSelectionInfo.value.mode === 'each';
    const enableSubRow = isEachMode ? false : rowSelectionInfo.value.enableSubRowCheck;

    return {
      enableRowSelection: (row) => {
        if (isDetailRow(row)) {
          return false;
        }
        const rowOriginal = row.original as Record<string, unknown>;
        const isDisabled = props.disabledRows?.some((disabledRow) => {
          const disabledObj = disabledRow as Record<string, unknown>;
          const keys = Object.keys(disabledObj).filter((k) => k !== 'rowId');

          return keys.every((key) => {
            const rowValue = rowOriginal[key];
            const disabledValue = disabledObj[key];

            if (Array.isArray(rowValue) && Array.isArray(disabledValue)) {
              return JSON.stringify(rowValue) === JSON.stringify(disabledValue);
            }

            return rowValue === disabledValue;
          });
        });
        return !isDisabled;
      },
      enableMultiRowSelection: rowSelectionInfo.value.useMultiCheck,
      enableSubRowSelection: enableSubRow,
      onRowSelectionChange: updateRowSelectionState,
    };
  });

  /**
   * 트리 모드에서 자식 체크 동기화 대상이 되는 부모 row 인지 판정
   * 셀 체크박스 토글 핸들러와 effectively-selected 판정에서 공통으로 사용
   */
  const isTreeParentRow = (row: GridRowInstance<RowDataType>): boolean => {
    return !!(
      treeViewInfo.value?.use &&
      rowSelectionInfo.value.enableSubRowCheck &&
      rowSelectionInfo.value.mode !== 'each' &&
      rowSelectionInfo.value.mode !== 'parentOnly' &&
      row.subRows?.length
    );
  };

  /**
   * 트리 모드에서 부모 row는 state에 직접 포함되지 않으므로,
   * 자식이 모두 선택된 경우 부모도 선택된 것으로 간주하는 헬퍼
   * 셀 체크박스(ModernGrid.vue)의 `getIsAllSubRowsSelected()` 판정과 동일한 정책을 사용
   */
  const isRowEffectivelySelected = (
    row: GridRowInstance<RowDataType>,
    state: RowSelectionState,
  ): boolean => {
    if (state[row.id] === true) return true;
    if (isTreeParentRow(row)) {
      return row.getIsAllSubRowsSelected();
    }
    return false;
  };

  /**
   * 셀 체크박스 클릭 시 트리 구조를 인지하여 토글하는 핸들러
   * 자식이 모두 체크되어 부모가 시각적으로만 체크된 상태(state에는 부모가 없음)에서
   * 부모를 클릭하면 TanStack의 no-op 가드(isSelected === value)에 걸려
   * 자식이 해제되지 않으므로, 자식들을 명시적으로 false로 토글
   */
  const toggleRowCheckbox = (row: GridRowInstance<RowDataType>, e: Event) => {
    if (!row.getCanSelect()) return;

    const targetChecked = (e.target as HTMLInputElement | null)?.checked ?? !row.getIsSelected();

    if (
      isTreeParentRow(row) &&
      !targetChecked &&
      !row.getIsSelected() &&
      row.getIsAllSubRowsSelected()
    ) {
      row.subRows.forEach((subRow) => subRow.toggleSelected(false));
      return;
    }

    row.toggleSelected(targetChecked);
  };

  // 헤더 체크박스 클릭 시 disabled/uncheckable 행을 제외한 전체 선택/해제
  const toggleAllSelectableRows = () => {
    const currentState = rowSelectionState.value;
    const newState: RowSelectionState = { ...currentState };

    const rows = table.value?.getRowModel().flatRows ?? [];
    const selectableRows = rows.filter((row) => row.getCanSelect() && !isRowUncheckable(row));

    const isAllSelected =
      selectableRows.length > 0 &&
      selectableRows.every((row) => isRowEffectivelySelected(row, currentState));

    if (!isAllSelected) {
      selectableRows.forEach((row) => {
        newState[row.id] = true;
      });
    } else {
      selectableRows.forEach((row) => {
        delete newState[row.id];
      });
    }

    updateRowSelectionState(newState);
  };

  // 선택 가능한 모든 행이 선택되었는지 확인
  const isAllSelectableRowsSelected = computed(() => {
    const currentState = rowSelectionState.value;
    const rows = table.value?.getRowModel().flatRows ?? [];
    const selectableRows = rows.filter((row) => row.getCanSelect() && !isRowUncheckable(row));

    if (selectableRows.length === 0) return false;

    return selectableRows.every((row) => isRowEffectivelySelected(row, currentState));
  });

  // 선택 가능한 행 중 일부만 선택되었는지 확인 (indeterminate 상태)
  const isSomeSelectableRowsSelected = computed(() => {
    const currentState = rowSelectionState.value;
    const rows = table.value?.getRowModel().flatRows ?? [];
    const selectableRows = rows.filter((row) => row.getCanSelect() && !isRowUncheckable(row));

    if (selectableRows.length === 0) return false;

    const selectedCount = selectableRows.filter((row) =>
      isRowEffectivelySelected(row, currentState),
    ).length;
    return selectedCount > 0 && selectedCount < selectableRows.length;
  });

  // 모든 행이 uncheckable인지 확인 (헤더 체크박스 비활성화용)
  const isAllRowsUncheckable = computed(() => {
    const rows = table.value?.getRowModel().flatRows ?? [];
    if (rows.length === 0) return true;

    // 체크 가능한 row가 하나라도 있으면 false
    return rows.every((row) => !row.getCanSelect() || isRowUncheckable(row));
  });

  // 그리드 내부 업데이트 여부 플래그 (watch에서 불필요한 O(n²) 재계산 방지)
  const isInternalCheckedRowsUpdate = ref(false);

  const isSameSelectionState = (a: RowSelectionState, b: RowSelectionState): boolean => {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    return aKeys.length === bKeys.length && bKeys.every((key) => a[key] === b[key]);
  };

  const createSelectionStateFromCheckedRows = (): RowSelectionState => {
    const newState: RowSelectionState = {};

    if (!props.checkedRows?.length) {
      return newState;
    }

    if (rowSelectionInfo.value.useMultiCheck === false) {
      const firstCheckedRow = props.checkedRows[0];
      const rowId = getRowIdByData<RowDataType>(
        firstCheckedRow,
        rowsMapById.value,
        expandingKey.value,
      );
      if (rowId) {
        newState[rowId] = true;
      }
      return newState;
    }

    props.checkedRows.forEach((checkedRow) => {
      const rowId = getRowIdByData<RowDataType>(checkedRow, rowsMapById.value, expandingKey.value);
      if (rowId) {
        newState[rowId] = true;
      }
    });

    return newState;
  };

  const isCheckedRowsExternallyChanged = (): boolean => {
    const expectedState = createSelectionStateFromCheckedRows();
    return !isSameSelectionState(rowSelectionState.value, expectedState);
  };

  const setRowSelectionState = () => {
    const newState = createSelectionStateFromCheckedRows();
    if (isSameSelectionState(rowSelectionState.value, newState)) return;
    rowSelectionState.value = newState;
    table.value?.setRowSelection(newState);
  };

  watch(
    () => selectedRowId.value,
    (newVal, oldVal) => {
      if (newVal) {
        clickedRowId.value = newVal;
      } else if (oldVal && clickedRowId.value === oldVal) {
        clickedRowId.value = '';
      }
    },
  );

  return {
    rowSelectionInfo,
    rowSelectionState,
    highlightedRowIds,
    selectedRowId,
    clickedRowId,
    rowSelectionOptions,
    selectRow,
    onClickRow,
    setRowSelectionState,
    isCheckedRowsExternallyChanged,
    isInternalCheckedRowsUpdate,
    isRowUncheckable,
    toggleAllSelectableRows,
    toggleRowCheckbox,
    isAllSelectableRowsSelected,
    isSomeSelectableRowsSelected,
    isAllRowsUncheckable,
  };
};
